// src/shared/services/featureRequestService.ts
import apiService from '@core/api/apiService';

const BASE = '/v1/user/feature-requests';

export type FeatureRequestStatus = 'pending' | 'in_progress' | 'done' | 'rejected';

export interface FeatureReply {
  id: string;
  userId: string | number;
  authorName: string;
  body: string;
  createdAt: string;
}

/** App-facing shape (camelCase). */
export interface FeatureRequest {
  id: string | number;
  userId: string | number;
  submittedBy: {
    id: string | number;
    name: string;
  };
  isOwner: boolean;
  pageName: string;
  title: string;
  description: string;
  steps?: string;
  url?: string;
  status: FeatureRequestStatus;
  replies: FeatureReply[];
  createdAt: string;
}

function fromApi(raw: any): FeatureRequest {
  return {
    id: raw.id,
    userId: raw.userId ?? raw.user_id ?? '',
    submittedBy: {
      id: raw.submittedBy?.id ?? raw.submitted_by?.id ?? raw.userId ?? raw.user_id ?? '',
      name: raw.submittedBy?.name ?? raw.submitted_by?.name ?? 'کاربر نامشخص',
    },
    isOwner: Boolean(raw.isOwner ?? raw.is_owner),
    pageName: raw.pageName ?? raw.page_name ?? '',
    title: raw.title ?? '',
    description: raw.description ?? '',
    steps: raw.steps,
    url: raw.url,
    status: raw.status ?? 'pending',
    replies: Array.isArray(raw.replies)
      ? raw.replies.map((reply: any) => ({
          id: String(reply.id ?? ''),
          userId: reply.userId ?? reply.user_id ?? '',
          authorName: reply.authorName ?? reply.author_name ?? 'کاربر نامشخص',
          body: reply.body ?? '',
          createdAt: reply.createdAt ?? reply.created_at ?? new Date().toISOString(),
        }))
      : [],
    createdAt: raw.createdAt ?? raw.created_at ?? new Date().toISOString(),
  };
}

/** List all feature requests visible to authenticated users. */
export async function getFeatureRequests(): Promise<FeatureRequest[]> {
  const response = await apiService.get<any>(BASE);
  let list: any[] = [];
  if (Array.isArray(response)) list = response;
  else if (response?.data && Array.isArray(response.data)) list = response.data;
  else if (response?.feature_requests && Array.isArray(response.feature_requests)) list = response.feature_requests;
  return list.map(fromApi);
}

/** Create a feature request (associated to current user by backend). */
export async function createFeatureRequest(data: {
  page_name: string;
  title: string;
  description: string;
  steps?: string;
  url?: string;
}): Promise<FeatureRequest> {
  const response = await apiService.post<any>(BASE, data);
  const raw = response?.data ?? response;
  return raw ? fromApi(raw) : fromApi({ ...data, id: '', status: 'pending', created_at: new Date().toISOString() });
}

/** Update feature request status. */
export async function updateFeatureRequestStatus(id: string | number, status: FeatureRequestStatus): Promise<FeatureRequest> {
  const response = await apiService.patch<any>(`${BASE}/${id}`, { status });
  const raw = response?.data ?? response;
  return raw ? fromApi(raw) : ({ id, status } as FeatureRequest);
}

/** Update feature request (full fields). */
export async function updateFeatureRequest(
  id: string | number,
  data: { page_name?: string; title?: string; description?: string; steps?: string; url?: string }
): Promise<FeatureRequest> {
  const response = await apiService.patch<any>(`${BASE}/${id}`, data);
  const raw = response?.data ?? response;
  return raw ? fromApi(raw) : ({ id, ...data } as FeatureRequest);
}

/** Delete a feature request. */
export async function deleteFeatureRequest(id: string | number): Promise<void> {
  await apiService.delete(`${BASE}/${id}`);
}

/** Add a reply visible to every authenticated user. */
export async function replyToFeatureRequest(id: string | number, body: string): Promise<FeatureRequest> {
  const response = await apiService.post<any>(`${BASE}/${id}/replies`, { body });
  const raw = response?.data ?? response;
  return raw ? fromApi(raw) : fromApi({ id, replies: [] });
}
